import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, select } from '@ngrx/store';
import { concatMap, map, mergeMap, of, switchMap } from 'rxjs';

import { AddressService } from 'ish-core/services/address/address.service';
import { BasketService } from 'ish-core/services/basket/basket.service';
import { CustomBasketService } from 'ish-core/services/custom-basket/custom-basket.service';
import { updateCustomerAddressFail, updateCustomerAddressSuccess } from 'ish-core/store/customer/addresses';
import { getLoggedInCustomer } from 'ish-core/store/customer/user';
import { triggerGTMRemoveToCart } from 'ish-core/store/shopping/gtm/google-tag-manager.actions';
import { mapErrorToAction, mapToPayload, mapToPayloadProperty } from 'ish-core/utils/operators';

import {
  assignBasketAddress,
  createBasketSuccess,
  loadBasket,
  loadBasketEligiblePaymentMethods,
  loadBasketEligibleShippingMethods,
  loadBasketEligibleShippingMethodsFail,
  loadBasketEligibleShippingMethodsSuccess,
  loadBasketFail,
  loadBasketWithId,
  mergeBasketSuccess,
  resetBasketErrors,
  validateBasketAfterCheckoutDataChange,
} from './basket.actions';
import {
  customDeleteBasket,
  customDeleteBasketFail,
  customDeleteBasketSuccess,
  customLoadBasketEligibleShippingMethodsWithBasket,
  customLoadBasketWithShippingMethods,
  customLoadBasketWithShippingMethodsSuccess,
  customUpdateBasketAddress,
} from './custom-basket.actions';

@Injectable()
export class CustomBasketEffects {
  constructor(
    private actions$: Actions,
    private customBasketService: CustomBasketService,
    private basketService: BasketService,
    private store: Store,
    private addressService: AddressService
  ) {}

  /**
   * Delete basket effect.
   */

  deleteBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customDeleteBasket),
      concatMap(() =>
        this.customBasketService.deleteBasket().pipe(
          switchMap(() =>
            this.basketService
              .createBasket()
              .pipe(
                mergeMap(basket => [
                  triggerGTMRemoveToCart(),
                  customDeleteBasketSuccess(),
                  createBasketSuccess({ basket }),
                ])
              )
          ),
          mapErrorToAction(customDeleteBasketFail)
        )
      )
    )
  );

  /**
   * Load basket effect with Shipping Methods.
   */
  loadBasketWithShippingMethods$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customLoadBasketWithShippingMethods),
      mergeMap(() =>
        !SSR && window.sessionStorage.getItem('basket-id')
          ? of(loadBasketWithId({ basketId: window.sessionStorage.getItem('basket-id') }))
          : this.basketService.getBasket().pipe(
              map(basket => customLoadBasketWithShippingMethodsSuccess({ basket })),
              mapErrorToAction(loadBasketFail)
            )
      )
    )
  );

  /**
   * Load Shipping methods after successful loading of basket
   */
  loadShippingMethodsAfterLoadingBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customLoadBasketWithShippingMethodsSuccess),
      mapToPayloadProperty('basket'),
      map(basket => customLoadBasketEligibleShippingMethodsWithBasket({ basket }))
    )
  );

  loadShippingAndPaymentMethodsAfterSuccessfulLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(mergeBasketSuccess),
      switchMap(() => [loadBasketEligibleShippingMethods(), loadBasketEligiblePaymentMethods()])
    )
  );
  /**
   * Load Shipping methods with basket Input
   */
  customLoadBasketEligibleShippingMethodsWithBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customLoadBasketEligibleShippingMethodsWithBasket),
      mapToPayloadProperty('basket'),
      concatMap(basket =>
        this.basketService.getBasketEligibleShippingMethods(basket.bucketId).pipe(
          concatMap(result => [
            loadBasketEligibleShippingMethodsSuccess({ shippingMethods: result }),
            validateBasketAfterCheckoutDataChange(),
          ]),
          mapErrorToAction(loadBasketEligibleShippingMethodsFail)
        )
      )
    )
  );

  customUpdateBasketAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customUpdateBasketAddress),
      mapToPayload(),
      concatLatestFrom(() => this.store.pipe(select(getLoggedInCustomer))),
      mergeMap(([payload, customer]) => {
        // create address at customer for logged in user
        if (customer) {
          return this.addressService.updateCustomerAddress('-', payload.address).pipe(
            mergeMap(() => [
              assignBasketAddress({ addressId: payload.address.id, scope: payload.scope }),
              updateCustomerAddressSuccess({ address: payload.address }),
              loadBasket(),
              customLoadBasketWithShippingMethods(),
              resetBasketErrors(),
            ]),
            mapErrorToAction(updateCustomerAddressFail)
          );
          // create address at basket for anonymous user
        } else {
          return this.basketService.updateBasketAddress(payload.address).pipe(
            mergeMap(() => [
              assignBasketAddress({ addressId: payload.address.id, scope: payload.scope }),
              updateCustomerAddressSuccess({ address: payload.address }),
              loadBasket(),
              customLoadBasketWithShippingMethods(),
              resetBasketErrors(),
            ]),
            mapErrorToAction(updateCustomerAddressFail)
          );
        }
      })
    )
  );
}
